import React, { useContext, ReactElement, ComponentProps } from 'react';

import PropTypes from 'prop-types';
import { DropResult } from 'react-beautiful-dnd';
import { IdType, UseColumnOrderInstanceProps } from 'react-table';

import {
  StylableComponent,
  stylePropType,
  useLocale,
} from '@adsk/alloy-react-helpers';
import { Flyout } from '@adsk/alloy-react-tooltip';

import TablePropTypes from '../propTypes';
import TableSizeThemeContext from '../TableSizeThemeContext';
import { ColumnStateSetter, TableSize } from '../types';
import TableConfigurationButton from './Button';
import DefaultMenuContent, {
  DefaultMenuContentProps,
} from './DefaultMenuContent';
import { getValidColumns } from './helpers';
import { TableConfigurationMenuColumnInstance } from './types';

type ReorderableColumn<D extends Record<string, unknown>> =
  UseColumnOrderInstanceProps<D> & {
    resetColumnOrder: () => void;
  };

type FixedColumn = {
  setColumnOrder?: never;
  resetColumnOrder?: never;
};

type ReorderingProps<D extends Record<string, unknown>> =
  | ReorderableColumn<D>
  | FixedColumn;

export type TableConfigurationMenuProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> &
    ReorderingProps<D> & {
      toggleHideAllColumns: (shouldHide?: boolean) => void;
      allColumns: TableConfigurationMenuColumnInstance<D>[];
      columns: TableConfigurationMenuColumnInstance<D>[];
      setColumnState?: ColumnStateSetter<D>;
      renderButton?: () => ReactElement;
      renderMenuContent?: (props: DefaultMenuContentProps<D>) => ReactElement;
      getFlyoutProps?: (
        props: ComponentProps<typeof Flyout>
      ) => ComponentProps<typeof Flyout>;
    };

const TableConfigurationMenu = <D extends Record<string, unknown>>({
  toggleHideAllColumns,
  allColumns,
  columns,
  setColumnOrder,
  resetColumnOrder,
  setColumnState,
  renderButton = () => <TableConfigurationButton />,
  renderMenuContent = (props) => <DefaultMenuContent<D> {...props} />,
  getFlyoutProps = (props) => props,
  style,
  className,
}: TableConfigurationMenuProps<D>): ReactElement => {
  const isColumnReorderable = !!setColumnOrder;
  const { setTableSizeTheme, resetTableSizeTheme } = useContext(
    TableSizeThemeContext
  );

  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  // TODO: this doesn't work, need to refresh to pick up the size reset
  // originalWidth is not preserved, maybe need to pull it from localStorage before clearing it?
  const resetColumnWidths = React.useCallback(() => {
    columns.forEach((column) => {
      column.width = column.originalWidth;
    });
  }, [columns]);

  const reset = React.useCallback(() => {
    toggleHideAllColumns(false);
    setColumnState?.({});
    resetTableSizeTheme();
    resetColumnOrder?.();
    resetColumnWidths?.();
  }, [
    toggleHideAllColumns,
    resetTableSizeTheme,
    resetColumnOrder,
    resetColumnWidths,
    setColumnState,
  ]);

  const setSize = React.useCallback(
    (size: TableSize) => {
      setTableSizeTheme(size);
      setColumnState?.((prevState) => {
        return { ...prevState, size };
      });
    },
    [setTableSizeTheme, setColumnState]
  );

  const validColumns = getValidColumns(allColumns);
  const handleOnDragEnd = (result: DropResult) => {
    if (result.destination?.index === undefined || !setColumnOrder) {
      return;
    }
    const reorderedColumns = Array.from(validColumns.map((c) => c.id));
    const [reorderedItem] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, reorderedItem);

    setColumnOrder(reorderedColumns);
    setColumnState?.((prevState) => {
      return { ...prevState, order: reorderedColumns };
    });
  };

  const handleToggleColumn = (
    column: TableConfigurationMenuColumnInstance<D>
  ) => {
    column.toggleHidden();
    setColumnState?.((prevState) => {
      const disabledColumnIds =
        prevState?.hiddenColumnIds || ({} as Record<IdType<D>, boolean>);
      disabledColumnIds[column.id] = !column.isVisible;
      return { ...prevState, hiddenColumnIds: disabledColumnIds };
    });
  };

  const densityLabel = T.translate('DENSITY');
  const resetLabel = T.translate('RESET_ALL');

  const flyoutProps = getFlyoutProps({
    content: renderMenuContent({
      handleToggleColumn,
      isColumnReorderable,
      handleOnDragEnd,
      validColumns,
      densityLabel,
      setSize,
      resetLabel,
      reset,
    }),
    placement: Flyout.PLACEMENTS.BOTTOM_END,
    shouldWrapChildren: true,
    containerPadding: 0,
  });

  return (
    <Flyout {...flyoutProps} style={style} className={className}>
      {renderButton()}
    </Flyout>
  );
};

TableConfigurationMenu.displayName = 'TableConfigurationMenu';

TableConfigurationMenu.propTypes = {
  /**
   * A function used to show or hide all columns on the table
   */
  toggleHideAllColumns: PropTypes.func.isRequired,
  /**
   * All of the columns generated when the table became initialized
   */
  allColumns: PropTypes.arrayOf(TablePropTypes.column).isRequired,
  /**
   * The initial set of column options passed into the table
   */
  columns: PropTypes.arrayOf(TablePropTypes.column).isRequired,
  /**
   * A function that sets the order of the columns from left to right
   */
  setColumnOrder: PropTypes.func,
  /**
   * A function that resets the column order to the original ordering from the column
   * options
   */
  resetColumnOrder: PropTypes.func,
  /**
   * A function that attaches pieces of state to specific column instances
   */
  setColumnState: PropTypes.func,
  /**
   * A render prop function that controls the rendering of the button which controls
   * opening and closing of the menu
   */
  renderButton: PropTypes.func,
  /**
   * A render prop function that controls the rendering of the entire menu's content.
   */
  renderMenuContent: PropTypes.func,
  /* Styles applied to the root element */
  style: stylePropType,
  /* Class applied to the root element */
  className: PropTypes.string,
};

export default TableConfigurationMenu;
